/**
 * CSV Parser Utility
 *
 * Parses CSV with automatic separator detection. Handles quoted fields that
 * contain embedded separators and embedded newlines (e.g. Amex export).
 */

/**
 * Split CSV text into logical rows. Newlines inside double-quoted fields
 * are treated as part of the field, not row boundaries.
 */
function splitCSVIntoRows(csvText: string): string[] {
  const rows: string[] = [];
  let currentRow = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentRow += '""';
        i++;
      } else {
        inQuotes = !inQuotes;
        currentRow += char;
      }
    } else if ((char === '\n' || char === '\r') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') i++;
      if (currentRow.trim().length > 0) {
        rows.push(currentRow);
      }
      currentRow = '';
    } else {
      currentRow += char;
    }
  }

  if (currentRow.trim().length > 0) {
    rows.push(currentRow);
  }
  return rows;
}

/**
 * Detect CSV separator by analyzing first few logical rows
 */
export function detectSeparator(csvText: string): string {
  const rows = splitCSVIntoRows(csvText).slice(0, 5).filter(r => r.trim().length > 0);
  if (rows.length === 0) return ',';

  let commaCount = 0;
  let semicolonCount = 0;
  let tabCount = 0;

  rows.forEach(row => {
    let inQuotes = false;
    for (let i = 0; i < row.length; i++) {
      const char = row[i];
      if (char === '"') inQuotes = !inQuotes;
      else if (!inQuotes) {
        if (char === ',') commaCount++;
        if (char === ';') semicolonCount++;
        if (char === '\t') tabCount++;
      }
    }
  });

  if (tabCount > commaCount && tabCount > semicolonCount) return '\t';
  if (semicolonCount > commaCount) return ';';
  return ',';
}

/**
 * Parse CSV line handling quoted fields
 * @param line - CSV line
 * @param separator - Separator character
 * @returns Array of field values
 */
function parseCSVLine(line: string, separator: string): string[] {
  const fields: string[] = [];
  let currentField = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        // Escaped quote
        currentField += '"';
        i++; // Skip next quote
      } else {
        // Toggle quote state
        inQuotes = !inQuotes;
      }
    } else if (char === separator && !inQuotes) {
      // End of field
      fields.push(currentField);
      currentField = '';
    } else {
      currentField += char;
    }
  }

  // Add last field
  fields.push(currentField);

  return fields;
}

/**
 * Parse CSV text into array of rows. Respects quoted fields with embedded newlines.
 */
export function parseCSV(csvText: string, separator?: string): string[][] {
  const detectedSeparator = separator || detectSeparator(csvText);
  const rows = splitCSVIntoRows(csvText).filter(row => row.trim().length > 0);
  return rows.map(row => parseCSVLine(row, detectedSeparator));
}

/**
 * Parse CSV file (from File object)
 * @param file - CSV file
 * @returns Promise resolving to parsed CSV data
 */
export async function parseCSVFile(file: File): Promise<{ columns: string[]; rows: string[][]; separator: string }> {
  const text = await file.text();
  const separator = detectSeparator(text);
  const rows = parseCSV(text, separator);

  if (rows.length === 0) {
    throw new Error('CSV file is empty');
  }

  const columns = rows[0]; // First row is header
  const dataRows = rows.slice(1); // Rest are data rows

  return {
    columns,
    rows: dataRows,
    separator
  };
}

